import { useState, useEffect, useRef } from 'react';
import './Admin.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PLATFORMS = ['Instagram', 'Facebook', 'YouTube', 'LinkedIn', 'Twitter'];
const TAGS      = ['Social Media', 'Story', 'Reel', 'Ad Campaign', 'Logo', 'Broucher', 'Website', 'Branding'];
const BLOG_CATS = ['Digital Marketing', 'Web Development', 'AI & Technology', 'Social Media', 'Graphic Design', 'eCommerce'];

export default function Admin() {
  const [token,  setToken]  = useState(localStorage.getItem('ar_admin_token') || '');
  const [authed, setAuthed] = useState(false);
  const [tab,    setTab]    = useState('dashboard');
  const [msg,    setMsg]    = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Portfolio
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [title,    setTitle]    = useState('');
  const [platform, setPlatform] = useState('Instagram');
  const [tag,      setTag]      = useState('Social Media');
  const [grp,      setGrp]      = useState('');
  const [caption,  setCaption]  = useState('');
  const [images,   setImages]   = useState([]);
  const [preview,  setPreview]  = useState([]);
  const fileRef = useRef();

  // Inbox
  const [contacts,    setContacts]    = useState([]);
  const [inboxLoad,   setInboxLoad]   = useState(false);
  const [expanded,    setExpanded]    = useState(null);

  // Blog
  const [posts,       setPosts]       = useState([]);
  const [postsLoad,   setPostsLoad]   = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [pTitle,      setPTitle]      = useState('');
  const [pCat,        setPCat]        = useState(BLOG_CATS[0]);
  const [pDate,       setPDate]       = useState('');
  const [pDesc,       setPDesc]       = useState('');
  const [pContent,    setPContent]    = useState('');
  const [pImage,      setPImage]      = useState(null);
  const [pPreview,    setPPreview]    = useState('');
  const [pMsg,        setPMsg]        = useState('');
  const blogFileRef = useRef();

  // Chat logs
  const [chatLogs,     setChatLogs]     = useState([]);
  const [chatLoad,     setChatLoad]     = useState(false);
  const [chatExpanded, setChatExpanded] = useState(null);

  // Chat leads (name/phone/email captured before chat starts)
  const [leads,     setLeads]     = useState([]);
  const [leadsLoad, setLeadsLoad] = useState(false);

  // ── Auth ──────────────────────────────────────────────────────────────────
  async function login(tok) {
    const t = tok || token;
    try {
      const r = await fetch(`${API_BASE}/api/admin/verify`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (r.ok) {
        localStorage.setItem('ar_admin_token', t);
        setAuthed(true);
        fetchItems(t);
        fetchContacts(t);
        fetchPosts();
        fetchLeads();
      } else {
        setMsg('❌ Wrong token');
      }
    } catch {
      setMsg('❌ Cannot connect to backend');
    }
  }

  function logout() {
    localStorage.removeItem('ar_admin_token');
    setAuthed(false);
    setToken('');
    setItems([]);
    setContacts([]);
    setPosts([]);
    setChatLogs([]);
    setLeads([]);
  }

  // ── Portfolio fetch ────────────────────────────────────────────────────────
  async function fetchItems(tok) {
    const t = tok || token;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/portfolio`);
      const d = await r.json();
      const normalized = d.map(p => ({
        ...p,
        src: p.src.startsWith('/uploads/') ? `${API_BASE}${p.src}` : p.src
      }));
      setItems(normalized);
    } catch {}
    setLoading(false);
  }

  // ── Inbox fetch ────────────────────────────────────────────────────────────
  async function fetchContacts(tok) {
    const t = tok || token;
    setInboxLoad(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/contacts`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) { setInboxLoad(false); return; }
      const d = await r.json();
      setContacts(d);
    } catch (e) {
      console.error('fetchContacts error:', e);
    }
    setInboxLoad(false);
  }

  async function markRead(id) {
    await fetch(`${API_BASE}/api/admin/contacts/${id}/read`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}` },
    });
    setContacts(cs => cs.map(c => c.id === id ? { ...c, is_read: 1 } : c));
  }

  async function deleteContact(id) {
    if (!window.confirm('Delete this message?')) return;
    await fetch(`${API_BASE}/api/admin/contacts/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setContacts(cs => cs.filter(c => c.id !== id));
    if (expanded === id) setExpanded(null);
  }

  function toggleExpand(contact) {
    if (expanded === contact.id) {
      setExpanded(null);
    } else {
      setExpanded(contact.id);
      if (!contact.is_read) markRead(contact.id);
    }
  }

  useEffect(() => {
    const saved = localStorage.getItem('ar_admin_token');
    if (saved) login(saved);
    // eslint-disable-next-line
  }, []);

  // ── Image picker ───────────────────────────────────────────────────────────
  function handleFiles(e) {
    const files = Array.from(e.target.files);
    setImages(files);
    setPreview(files.map(f => URL.createObjectURL(f)));
  }

  // ── Upload ─────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!title || !grp || images.length === 0) {
      setMsg('⚠️ Title, Group, and at least one Image required');
      return;
    }
    setMsg('Uploading...');
    let uploaded = 0;
    for (const img of images) {
      const fd = new FormData();
      fd.append('title', title); fd.append('platform', platform);
      fd.append('tag', tag);     fd.append('grp', grp);
      fd.append('caption', caption); fd.append('image', img);
      const r = await fetch(`${API_BASE}/api/admin/portfolio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (r.ok) uploaded++;
    }
    setMsg(`✅ Uploaded ${uploaded}/${images.length} images`);
    setTitle(''); setGrp(''); setCaption('');
    setImages([]); setPreview([]);
    if (fileRef.current) fileRef.current.value = '';
    fetchItems();
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this item?')) return;
    await fetch(`${API_BASE}/api/admin/portfolio/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchItems();
  }

  // ── Blog fetch ──────────────────────────────────────────────────────────────
  async function fetchPosts() {
    setPostsLoad(true);
    try {
      const r = await fetch(`${API_BASE}/api/blog`);
      const d = await r.json();
      const normalized = d.map(p => ({
        ...p,
        img: p.img.startsWith('/uploads/') ? `${API_BASE}${p.img}` : p.img
      }));
      setPosts(normalized);
    } catch {}
    setPostsLoad(false);
  }

  function handleBlogImage(e) {
    const file = e.target.files[0];
    setPImage(file || null);
    setPPreview(file ? URL.createObjectURL(file) : '');
  }

  function todayFormatted() {
    return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  function resetBlogForm() {
    setEditingId(null);
    setPTitle(''); setPCat(BLOG_CATS[0]); setPDate('');
    setPDesc(''); setPContent(''); setPImage(null); setPPreview('');
    if (blogFileRef.current) blogFileRef.current.value = '';
  }

  function startEditPost(post) {
    setEditingId(post.id);
    setPTitle(post.title); setPCat(post.cat);
    setPDate(post.date);
    setPDesc(post.desc); setPContent(post.content || ''); setPImage(null); setPPreview(post.img);
    setPMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleBlogSubmit() {
    if (!pTitle || !pDesc || (!editingId && !pImage)) {
      setPMsg('⚠️ Title, Description, and an Image are required');
      return;
    }
    setPMsg(editingId ? 'Saving...' : 'Publishing...');
    const fd = new FormData();
    fd.append('title', pTitle);
    fd.append('cat', pCat);
    fd.append('post_date', pDate || todayFormatted());
    fd.append('desc', pDesc);
    fd.append('content', pContent);
    if (pImage) fd.append('image', pImage);

    const url    = editingId ? `${API_BASE}/api/admin/blog/${editingId}` : `${API_BASE}/api/admin/blog`;
    const method = editingId ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (r.ok) {
      setPMsg(editingId ? '✅ Post updated' : '✅ Post published');
      resetBlogForm();
      fetchPosts();
    } else {
      setPMsg('❌ Something went wrong');
    }
  }

  async function handleDeletePost(id) {
    if (!window.confirm('Delete this blog post?')) return;
    await fetch(`${API_BASE}/api/admin/blog/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchPosts();
  }

  // ── Chat logs ───────────────────────────────────────────────────────────────
  async function fetchChatLogs() {
    setChatLoad(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/chat-logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setChatLogs(await r.json());
    } catch {}
    setChatLoad(false);
  }

  async function deleteChatLog(id) {
    await fetch(`${API_BASE}/api/admin/chat-logs/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setChatLogs(cs => cs.filter(c => c.id !== id));
  }

  async function clearAllChatLogs() {
    if (!window.confirm('Delete ALL chat logs? This cannot be undone.')) return;
    await fetch(`${API_BASE}/api/admin/chat-logs`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setChatLogs([]);
  }

  // Group chat logs by visitor session, most recent session first
  const chatSessions = (() => {
    const bySession = {};
    for (const log of chatLogs) {
      if (!bySession[log.session_id]) bySession[log.session_id] = [];
      bySession[log.session_id].push(log);
    }
    return Object.entries(bySession)
      .map(([sid, logs]) => ({
        sid,
        logs: logs.slice().sort((a, b) => a.id - b.id),
        latest: Math.max(...logs.map(l => l.id)),
      }))
      .sort((a, b) => b.latest - a.latest);
  })();

  // ── Leads ───────────────────────────────────────────────────────────────────
  async function fetchLeads() {
    setLeadsLoad(true);
    try {
      const r = await fetch(`${API_BASE}/api/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setLeads(await r.json());
    } catch {}
    setLeadsLoad(false);
  }

  async function deleteLead(id) {
    if (!window.confirm('Delete this lead?')) return;
    await fetch(`${API_BASE}/api/admin/leads/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    setLeads(ls => ls.filter(l => l.id !== id));
  }

  // Lookup: session_id -> lead, so the Chats tab can show who each visitor was
  const leadBySession = leads.reduce((acc, l) => { acc[l.session_id] = l; return acc; }, {});

  // Split contacts by where they came from: the plain Contact page vs the
  // "Find Your Service" wizard, so the admin panel can list them separately.
  const contactMessages  = contacts.filter(c => c.source !== 'wizard');
  const serviceRequests  = contacts.filter(c => c.source === 'wizard');
  const contactUnread    = contactMessages.filter(c => !c.is_read).length;
  const serviceUnread    = serviceRequests.filter(c => !c.is_read).length;

  const grouped = items.reduce((acc, item) => {
    if (!acc[item.grp]) acc[item.grp] = [];
    acc[item.grp].push(item);
    return acc;
  }, {});

  // ── Login ──────────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="admin-login">
        <div className="admin-login-box">
          <div className="admin-logo">🌿 AerialRoot Admin</div>
          <h2>Portfolio Manager</h2>
          <input
            type="password"
            placeholder="Admin Token"
            value={token}
            onChange={e => setToken(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && login()}
          />
          <button onClick={() => login()}>Login</button>
          {msg && <p className="admin-msg">{msg}</p>}
        </div>
      </div>
    );
  }

  // ── Panel ──────────────────────────────────────────────────────────────────
  const TAB_LABELS = {
    dashboard: '📊 Dashboard',
    portfolio: '🎨 Portfolio',
    blog:      '✍️ Blog',
    leads:     '👤 Leads',
    chats:     '💬 Chats',
    services:  '🧭 Find Your Service',
    inbox:     '📩 Contact Messages',
  };

  function goTab(t) {
    setTab(t);
    setSidebarOpen(false);
    if (t === 'blog')     fetchPosts();
    if (t === 'leads')    fetchLeads();
    if (t === 'chats')    { fetchChatLogs(); fetchLeads(); }
    if (t === 'inbox')    fetchContacts();
    if (t === 'services') fetchContacts();
    if (t === 'dashboard') { fetchItems(); fetchPosts(); fetchLeads(); fetchContacts(); fetchChatLogs(); }
  }

  return (
    <div className="admin-wrap admin-wrap-sidebar">
      <button className="admin-menu-toggle" onClick={() => setSidebarOpen(o => !o)}>☰</button>

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-logo admin-sidebar-logo">🌿 AerialRoot Admin</div>

        <nav className="admin-nav">
          <button className={`admin-navlink ${tab === 'dashboard' ? 'active' : ''}`}
            onClick={() => goTab('dashboard')}>
            📊 Dashboard
          </button>
          <button className={`admin-navlink ${tab === 'portfolio' ? 'active' : ''}`}
            onClick={() => goTab('portfolio')}>
            🎨 Portfolio
          </button>
          <button className={`admin-navlink ${tab === 'blog' ? 'active' : ''}`}
            onClick={() => goTab('blog')}>
            ✍️ Blog
          </button>
          <button className={`admin-navlink ${tab === 'leads' ? 'active' : ''}`}
            onClick={() => goTab('leads')}>
            👤 Leads
          </button>
          <button className={`admin-navlink ${tab === 'chats' ? 'active' : ''}`}
            onClick={() => goTab('chats')}>
            💬 Chats
          </button>
          <button className={`admin-navlink ${tab === 'services' ? 'active' : ''}`}
            onClick={() => goTab('services')}>
            🧭 Find Your Service
            {serviceUnread > 0 && <span className="unread-badge">{serviceUnread}</span>}
          </button>
          <button className={`admin-navlink ${tab === 'inbox' ? 'active' : ''}`}
            onClick={() => goTab('inbox')}>
            📩 Contact Messages
            {contactUnread > 0 && <span className="unread-badge">{contactUnread}</span>}
          </button>
        </nav>

        <button className="admin-logout admin-sidebar-logout" onClick={logout}>🚪 Logout</button>
      </aside>

      {sidebarOpen && <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="admin-main">
        <header className="admin-topbar">
          <span className="admin-topbar-title">{TAB_LABELS[tab]}</span>
        </header>

        <div className="admin-body">

        {/* ══ DASHBOARD TAB ══ */}
        {tab === 'dashboard' && (
          <div className="admin-dashboard">
            <div className="dash-grid">
              <div className="dash-card">
                <span className="dash-icon">🎨</span>
                <span className="dash-value">{items.length}</span>
                <span className="dash-label">Portfolio Images</span>
              </div>
              <div className="dash-card">
                <span className="dash-icon">🗂️</span>
                <span className="dash-value">{Object.keys(grouped).length}</span>
                <span className="dash-label">Portfolio Groups</span>
              </div>
              <div className="dash-card">
                <span className="dash-icon">✍️</span>
                <span className="dash-value">{posts.length}</span>
                <span className="dash-label">Blog Posts</span>
              </div>
              <div className="dash-card">
                <span className="dash-icon">👤</span>
                <span className="dash-value">{leads.length}</span>
                <span className="dash-label">Chat Leads</span>
              </div>
              <div className="dash-card">
                <span className="dash-icon">💬</span>
                <span className="dash-value">{chatSessions.length}</span>
                <span className="dash-label">Chat Visitors</span>
              </div>
              <div className={`dash-card ${serviceUnread > 0 ? 'dash-card-alert' : ''}`}>
                <span className="dash-icon">🧭</span>
                <span className="dash-value">{serviceRequests.length}</span>
                <span className="dash-label">
                  Find Your Service{serviceUnread > 0 ? ` (${serviceUnread} unread)` : ''}
                </span>
              </div>
              <div className={`dash-card ${contactUnread > 0 ? 'dash-card-alert' : ''}`}>
                <span className="dash-icon">📩</span>
                <span className="dash-value">{contactMessages.length}</span>
                <span className="dash-label">
                  Contact Messages{contactUnread > 0 ? ` (${contactUnread} unread)` : ''}
                </span>
              </div>
            </div>

            <div className="admin-card">
              <h3>🕘 Recent Contact Messages</h3>
              {contactMessages.length === 0 && <p className="inbox-empty">No messages yet.</p>}
              <div className="dash-recent-list">
                {contactMessages.slice(0, 5).map(c => (
                  <div key={c.id} className="dash-recent-item" onClick={() => goTab('inbox')}>
                    <span className={!c.is_read ? 'inbox-dot' : ''} />
                    <span className="dash-recent-name">{c.name}</span>
                    <span className="dash-recent-msg">{c.message}</span>
                    <span className="dash-recent-time">
                      {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="admin-card">
              <h3>👤 Recent Chat Leads</h3>
              {leads.length === 0 && <p className="inbox-empty">No leads yet.</p>}
              <div className="dash-recent-list">
                {leads.slice(0, 5).map(l => (
                  <div key={l.id} className="dash-recent-item" onClick={() => goTab('leads')}>
                    <span className="dash-recent-name">{l.name}</span>
                    <span className="dash-recent-msg">{l.phone || l.email || '—'}</span>
                    <span className="dash-recent-time">
                      {new Date(l.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ PORTFOLIO TAB ══ */}
        {tab === 'portfolio' && (
          <>
            <div className="admin-card">
              <h3>➕ Add Portfolio Items</h3>
              <div className="admin-form">
                <div className="admin-row">
                  <label>Client / Title</label>
                  <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. British School" />
                </div>
                <div className="admin-row">
                  <label>Group Name</label>
                  <input value={grp} onChange={e => setGrp(e.target.value)} placeholder="e.g. british-school" />
                  <small>Same group = same card in portfolio</small>
                </div>
                <div className="admin-row2">
                  <div>
                    <label>Platform</label>
                    <select value={platform} onChange={e => setPlatform(e.target.value)}>
                      {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label>Tag / Type</label>
                    <select value={tag} onChange={e => setTag(e.target.value)}>
                      {TAGS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="admin-row">
                  <label>Caption (optional)</label>
                  <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Short description..." />
                </div>
                <div className="admin-row">
                  <label>Images (multiple allowed)</label>
                  <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleFiles} />
                </div>
                {preview.length > 0 && (
                  <div className="admin-preview-row">
                    {preview.map((src, i) => <img key={i} src={src} alt="" className="admin-preview-img" />)}
                  </div>
                )}
                <button className="admin-submit" onClick={handleSubmit}>
                  Upload {images.length > 1 ? `${images.length} Images` : 'Image'}
                </button>
                {msg && <p className="admin-msg">{msg}</p>}
              </div>
            </div>

            <div className="admin-card">
              <h3>📁 All Portfolio Items ({items.length})</h3>
              {loading && <p>Loading...</p>}
              {Object.entries(grouped).map(([grpName, grpItems]) => (
                <div key={grpName} className="admin-group">
                  <div className="admin-group-header">
                    <span className="admin-group-name">📂 {grpName}</span>
                    <span className="admin-group-count">{grpItems.length} image{grpItems.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="admin-group-grid">
                    {grpItems.map(item => (
                      <div key={item.id} className="admin-item">
                        <img src={item.src} alt={item.title} className="admin-item-img" />
                        <div className="admin-item-info">
                          <span className="admin-item-title">{item.title}</span>
                          <span className="admin-item-meta">{item.platform} · {item.tag}</span>
                          {item.caption && <span className="admin-item-caption">{item.caption}</span>}
                        </div>
                        <button className="admin-delete" onClick={() => handleDelete(item.id)}>🗑</button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ══ BLOG TAB ══ */}
        {tab === 'blog' && (
          <>
            <div className="admin-card">
              <h3>{editingId ? '✏️ Edit Post' : '➕ Add Blog Post'}</h3>
              <div className="admin-form">
                <div className="admin-row">
                  <label>Title</label>
                  <input value={pTitle} onChange={e => setPTitle(e.target.value)} placeholder="e.g. 10 SEO Strategies for 2026" />
                </div>
                <div className="admin-row">
                  <label>Category</label>
                  <select value={pCat} onChange={e => setPCat(e.target.value)}>
                    {BLOG_CATS.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="admin-row">
                  <label>Date</label>
                  <input value={pDate} onChange={e => setPDate(e.target.value)} placeholder={todayFormatted()} />
                  <small>Leave blank to use today's date</small>
                </div>
                <div className="admin-row">
                  <label>Description / Excerpt</label>
                  <textarea rows="3" value={pDesc} onChange={e => setPDesc(e.target.value)} placeholder="Short summary shown on the blog card..." />
                </div>
                <div className="admin-row">
                  <label>Full Article Content</label>
                  <textarea rows="8" value={pContent} onChange={e => setPContent(e.target.value)} placeholder="Full article text shown on the blog detail page. Leave blank to reuse the excerpt above. Use blank lines to separate paragraphs." />
                </div>
                <div className="admin-row">
                  <label>Cover Image {editingId && '(leave empty to keep current)'}</label>
                  <input ref={blogFileRef} type="file" accept="image/*" onChange={handleBlogImage} />
                </div>
                {pPreview && (
                  <div className="admin-preview-row">
                    <img src={pPreview} alt="" className="admin-preview-img" />
                  </div>
                )}
                <div className="admin-row2">
                  <button className="admin-submit" onClick={handleBlogSubmit}>
                    {editingId ? 'Save Changes' : 'Publish Post'}
                  </button>
                  {editingId && (
                    <button className="admin-tab" onClick={resetBlogForm}>Cancel Edit</button>
                  )}
                </div>
                {pMsg && <p className="admin-msg">{pMsg}</p>}
              </div>
            </div>

            <div className="admin-card">
              <h3>📰 All Blog Posts ({posts.length})</h3>
              {postsLoad && <p>Loading...</p>}
              <div className="admin-group-grid">
                {posts.map(p => (
                  <div key={p.id} className="admin-item">
                    <img src={p.img} alt={p.title} className="admin-item-img" />
                    <div className="admin-item-info">
                      <span className="admin-item-title">{p.title}</span>
                      <span className="admin-item-meta">{p.cat} · {p.date}</span>
                      {p.desc && <span className="admin-item-caption">{p.desc}</span>}
                    </div>
                    <div style={{display:'flex', gap:'0.4rem'}}>
                      <button className="admin-delete" onClick={() => startEditPost(p)}>✏️</button>
                      <button className="admin-delete" onClick={() => handleDeletePost(p.id)}>🗑</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ══ LEADS TAB ══ */}
        {tab === 'leads' && (
          <div className="admin-card">
            <div className="inbox-header-row">
              <h3>👤 Customer Details from Chatbot ({leads.length})</h3>
              <button className="admin-submit" style={{padding:'0.4rem 1rem',fontSize:'0.8rem'}}
                onClick={() => fetchLeads()}>🔄 Refresh</button>
            </div>
            {leadsLoad && <p style={{color:'#5f6a58',fontSize:'0.9rem'}}>Loading leads...</p>}
            {!leadsLoad && leads.length === 0 && (
              <p className="inbox-empty">No leads yet. Chatbot la oruthar name/phone/email kudutha udane inga varum.</p>
            )}
            <div className="inbox-list">
              {leads.map(l => (
                <div key={l.id} className="inbox-item">
                  <div className="inbox-row">
                    <div className="inbox-left">
                      <div className="inbox-meta">
                        <span className="inbox-name">{l.name}</span>
                        <span className="inbox-email">{l.email || '—'}</span>
                      </div>
                    </div>
                    <div className="inbox-right">
                      {l.phone && <span className="inbox-phone">{l.phone}</span>}
                      <span className="inbox-time">
                        {new Date(l.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                      <a className="inbox-reply" href={`mailto:${l.email}`}>✉️</a>
                      {l.phone && (
                        <a className="inbox-wa" href={`https://wa.me/91${l.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">💬</a>
                      )}
                      <button className="admin-delete" onClick={() => deleteLead(l.id)}>🗑</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ CHATS TAB ══ */}
        {tab === 'chats' && (
          <div className="admin-card">
            <div className="inbox-header-row">
              <h3>💬 Chatbot Conversations ({chatSessions.length} visitor{chatSessions.length !== 1 ? 's' : ''}, {chatLogs.length} messages)</h3>
              <div style={{display:'flex', gap:'0.5rem'}}>
                <button className="admin-submit" style={{padding:'0.4rem 1rem',fontSize:'0.8rem'}}
                  onClick={() => fetchChatLogs()}>🔄 Refresh</button>
                {chatLogs.length > 0 && (
                  <button className="admin-delete" style={{padding:'0.4rem 1rem',fontSize:'0.8rem'}}
                    onClick={clearAllChatLogs}>🗑 Clear All</button>
                )}
              </div>
            </div>
            {chatLoad && <p style={{color:'#5f6a58',fontSize:'0.9rem'}}>Loading chats...</p>}
            {!chatLoad && chatSessions.length === 0 && (
              <p className="inbox-empty">No chatbot messages yet. Site visitor oru question kekura udane inga varum.</p>
            )}
            <div className="inbox-list">
              {chatSessions.map(session => (
                <div key={session.sid} className="inbox-item">
                  <div className="inbox-row" onClick={() => setChatExpanded(chatExpanded === session.sid ? null : session.sid)}>
                    <div className="inbox-left">
                      <div className="inbox-meta">
                        <span className="inbox-name">
                          {leadBySession[session.sid] ? leadBySession[session.sid].name : `Visitor ${session.sid.slice(0, 8)}`}
                        </span>
                        <span className="inbox-email">
                          {leadBySession[session.sid] ? (leadBySession[session.sid].phone || leadBySession[session.sid].email) : `${session.logs.length} message${session.logs.length > 1 ? 's' : ''}`}
                        </span>
                      </div>
                    </div>
                    <div className="inbox-right">
                      <span className="inbox-time">
                        {new Date(session.logs[session.logs.length - 1].created_at).toLocaleString('en-IN', {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                  {chatExpanded === session.sid && (
                    <div className="inbox-body">
                      {session.logs.map(log => (
                        <div key={log.id} style={{marginBottom:'1rem', paddingBottom:'1rem', borderBottom:'1px solid #e3e8dd'}}>
                          <p className="inbox-msg-label">VISITOR · {log.source}</p>
                          <p className="inbox-msg-text">{log.user_message}</p>
                          <p className="inbox-msg-label" style={{marginTop:'0.6rem'}}>AERIALBOT</p>
                          <p className="inbox-msg-text">{log.bot_reply}</p>
                          <div className="inbox-actions">
                            <button className="admin-delete" onClick={() => deleteChatLog(log.id)}>🗑 Delete this message</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ FIND YOUR SERVICE TAB ══ */}
        {tab === 'services' && (
          <div className="admin-card">
            <div className="inbox-header-row">
              <h3>🧭 Find Your Service Requests ({serviceRequests.length})</h3>
              <button className="admin-submit" style={{padding:'0.4rem 1rem',fontSize:'0.8rem'}}
                onClick={() => fetchContacts()}>🔄 Refresh</button>
            </div>
            {inboxLoad && <p style={{color:'#5f6a58',fontSize:'0.9rem'}}>Loading requests...</p>}
            {!inboxLoad && serviceRequests.length === 0 && (
              <p className="inbox-empty">No service requests yet. "Find Your Service" wizard-la oruthar submit pannumbothu inga show aagum.</p>
            )}
            <div className="inbox-list">
              {serviceRequests.map(c => (
                <div key={c.id} className={`inbox-item ${!c.is_read ? 'unread' : ''}`}>
                  <div className="inbox-row" onClick={() => toggleExpand(c)}>
                    <div className="inbox-left">
                      {!c.is_read && <span className="inbox-dot" />}
                      <div className="inbox-meta">
                        <span className="inbox-name">{c.name}</span>
                        <span className="inbox-email">{c.service || '—'}</span>
                      </div>
                    </div>
                    <div className="inbox-right">
                      {c.phone && <span className="inbox-phone">{c.phone}</span>}
                      <span className="inbox-time">
                        {new Date(c.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                      <button className="admin-delete"
                        onClick={e => { e.stopPropagation(); deleteContact(c.id); }}>🗑</button>
                    </div>
                  </div>
                  {expanded === c.id && (
                    <div className="inbox-body">
                      <p className="inbox-msg-label">REQUEST DETAILS</p>
                      <p className="inbox-msg-text">{c.message}</p>
                      <div className="inbox-actions">
                        {c.phone && (
                          <a className="inbox-wa"
                            href={`https://wa.me/91${c.phone.replace(/\D/g, '')}`}
                            target="_blank" rel="noreferrer">
                            💬 WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ INBOX TAB ══ */}
        {tab === 'inbox' && (
          <div className="admin-card">
            <div className="inbox-header-row">
              <h3>📩 Contact Messages ({contactMessages.length})</h3>
              <button className="admin-submit" style={{padding:'0.4rem 1rem',fontSize:'0.8rem'}}
                onClick={() => fetchContacts()}>🔄 Refresh</button>
            </div>
            {inboxLoad && <p style={{color:'#5f6a58',fontSize:'0.9rem'}}>Loading messages...</p>}
            {!inboxLoad && contactMessages.length === 0 && (
              <p className="inbox-empty">No messages yet. Contact form submit pannumbothu inga show aagum.</p>
            )}
            <div className="inbox-list">
              {contactMessages.map(c => (
                <div key={c.id} className={`inbox-item ${!c.is_read ? 'unread' : ''}`}>
                  <div className="inbox-row" onClick={() => toggleExpand(c)}>
                    <div className="inbox-left">
                      {!c.is_read && <span className="inbox-dot" />}
                      <div className="inbox-meta">
                        <span className="inbox-name">{c.name}</span>
                        <span className="inbox-email">{c.email}</span>
                      </div>
                    </div>
                    <div className="inbox-right">
                      {c.phone && <span className="inbox-phone">{c.phone}</span>}
                      <span className="inbox-time">
                        {new Date(c.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </span>
                      <button className="admin-delete"
                        onClick={e => { e.stopPropagation(); deleteContact(c.id); }}>🗑</button>
                    </div>
                  </div>
                  {expanded === c.id && (
                    <div className="inbox-body">
                      <p className="inbox-msg-label">MESSAGE</p>
                      <p className="inbox-msg-text">{c.message}</p>
                      <div className="inbox-actions">
                        <a className="inbox-reply"
                          href={`mailto:${c.email}?subject=Re: Your inquiry — AerialRoot Digital`}>
                          ✉️ Reply via Email
                        </a>
                        {c.phone && (
                          <a className="inbox-wa"
                            href={`https://wa.me/91${c.phone.replace(/\D/g, '')}`}
                            target="_blank" rel="noreferrer">
                            💬 WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        </div>
      </div>
    </div>
  );
}
